import { useLinkBundledAccountMutation } from '@/services/accounts';
import { isErrorWithData } from '@/services/helpers';
import { Alert, Box, Button, Modal, TextInput, toast } from '@palmetto/palmetto-components';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { AccountDocument } from 'types';

interface CreateBundledAccountModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  sourceAccount: AccountDocument;
}

interface BundleFormValues {
  bundleWithAccountId: string;
}

const OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i;
const GENERIC_ERROR_MESSAGE = 'An unexpected error occurred. Please try again.';

export const CreateBundledAccountModal = ({ isOpen, onDismiss, sourceAccount }: CreateBundledAccountModalProps) => {
  const [linkBundledAccount] = useLinkBundledAccountMutation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<BundleFormValues>({ defaultValues: { bundleWithAccountId: '' } });

  const handleDismiss = useCallback(() => {
    reset();
    setSubmitError(null);
    onDismiss();
  }, [onDismiss, reset]);

  const onSubmit = useCallback(
    async (values: BundleFormValues) => {
      setSubmitError(null);
      try {
        const result = await linkBundledAccount({
          accountId: values.bundleWithAccountId.trim(),
          bundleWith: String(sourceAccount.id),
        }).unwrap();

        if (result.errors?.length) {
          setSubmitError(result.errors.map((e) => e.reason).join(' '));
          return;
        }

        handleDismiss();
        toast.success('Accounts bundled successfully');
      } catch (error) {
        setSubmitError(isErrorWithData(error) ? error.data.message : GENERIC_ERROR_MESSAGE);
      }
    },
    [linkBundledAccount, sourceAccount.id, handleDismiss],
  );

  return (
    <Modal ariaLabelledBy="bundleAccount" isOpen={isOpen} onDismiss={handleDismiss} maxWidth="480px">
      <Modal.Header id="bundleAccount" title="Bundle with Account" onDismiss={handleDismiss} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <Box gap="lg">
            <Box fontSize="sm" color="body-secondary">
              Enter the ID of the existing account to bundle with this one. Both accounts must belong to the same
              customer and organization, and have different program types.
            </Box>

            {submitError && <Alert variant="danger" hasIcon message={submitError} />}

            <Controller
              name="bundleWithAccountId"
              control={control}
              rules={{
                required: 'Account ID is required',
                pattern: { value: OBJECT_ID_PATTERN, message: 'Must be a valid 24-character account ID' },
                validate: (value) =>
                  value.toLowerCase() !== String(sourceAccount.id).toLowerCase() ||
                  'Cannot bundle an account with itself',
              }}
              render={({ field, fieldState }) => (
                <TextInput
                  id="bundleWithAccountId"
                  label="Account ID"
                  placeholder="e.g. 507f1f77bcf86cd799439011"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  isDisabled={isSubmitting}
                  isRequired
                  error={fieldState.error?.message}
                />
              )}
            />
          </Box>
        </Modal.Body>
        <Modal.Footer justifyContent="flex-end" childGap="sm">
          <Button type="button" variant="secondary" tone="neutral" onClick={handleDismiss} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isSubmitting} isDisabled={isSubmitting}>
            Bundle
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
